/**
 * Kroll Stop
 *
 * Trailing stop-loss indicator based on ATR.
 * Stop_High = HHV(ATR-adjusted high, p) − multiplier * ATR
 * Stop_Low  = LLV(ATR-adjusted low, p) + multiplier * ATR
 *
 * Category: Trend | Pane: Overlay
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { computeATR } from "./atr";

export const krollStopIndicator: IndicatorDefinition = {
  id: "kroll_stop",
  name: "Kroll Stop",
  shortName: "KrollStop",
  category: "trend",
  pane: "overlay",
  params: [
    { key: "atrPeriod", label: "ATR Period", type: "number", default: 10, min: 2, max: 100 },
    { key: "stopPeriod", label: "Stop Period", type: "number", default: 9, min: 2, max: 100 },
    { key: "multiplier", label: "Multiplier", type: "number", default: 1.0, min: 0.1, max: 10, step: 0.1 },
  ],
  plots: [
    { key: "stopHigh", label: "Stop High", color: "#F44336", style: "line", lineWidth: 2 },
    { key: "stopLow", label: "Stop Low", color: "#4CAF50", style: "line", lineWidth: 2 },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { atrPeriod, stopPeriod, multiplier } = params;
    const atr = computeATR(candles, atrPeriod);
    const highData: { time: number; value: number }[] = [];
    const lowData: { time: number; value: number }[] = [];

    // First pass: compute first stop references
    const firstHighStop: number[] = new Array(candles.length).fill(NaN);
    const firstLowStop: number[] = new Array(candles.length).fill(NaN);

    for (let i = 0; i < candles.length; i++) {
      if (atr[i] !== undefined) {
        firstHighStop[i] = candles[i].high - multiplier * atr[i];
        firstLowStop[i] = candles[i].low + multiplier * atr[i];
      }
    }

    // Second pass: highest of firstHighStop and lowest of firstLowStop over stopPeriod
    const startIdx = atrPeriod + stopPeriod - 1;
    for (let i = startIdx; i < candles.length; i++) {
      let maxHigh = -Infinity;
      let minLow = Infinity;
      let valid = true;

      for (let j = i - stopPeriod + 1; j <= i; j++) {
        if (isNaN(firstHighStop[j]) || isNaN(firstLowStop[j])) {
          valid = false;
          break;
        }
        if (firstHighStop[j] > maxHigh) maxHigh = firstHighStop[j];
        if (firstLowStop[j] < minLow) minLow = firstLowStop[j];
      }

      if (valid) {
        highData.push({ time: candles[i].time, value: maxHigh });
        lowData.push({ time: candles[i].time, value: minLow });
      }
    }

    return [
      { key: "stopHigh", data: highData },
      { key: "stopLow", data: lowData },
    ];
  },
};
